import axios, {
  AxiosError,
  AxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios';
import { config } from '@uhg-abyss/web/tools/config';
import {
  ApiResponse,
  HcpMemberSearchResponse,
  InitializeSessionResponse,
  MemberInfo,
  MemberHistoryResponse,
  RequestTokenResponse,
  SessionInfoResponse,
  UserInfoResponse,
  FindhelpCategoriesResponse,
  FindhelpAttributeTagsResponse,
  ProviderToUseResponse,
  FindhelpProgramsResponse,
  UniteUsFhirAuthRequest,
  FindhelpReferralRequest,
  PrimaryCategoriesResponse,
  BenefitPlan,
  BenefitDetailsResponse,
  Benefitcheck,
  BenefitSearchResponse,
  EligibleIncomeResponse,
  saveQuizResponse,
  RefreshTokenResponse,
  UpdateReferralRequest,
  UpdateScreenerRequest,
  SuggestProgramEmailRequest,
  GetAllFavoritesResponse,
  SaveAndDeleteFavoriteResponse,
  FindhelpProgramResponse,
  FeatureFlags,
  UniteUsProgramSearchResults,
  UniteUsService,
  UniteUSReferralRequest,
  UniteUsFilter,
  BenefitReferralRequest,
  BulkReferralResult,
} from './api.types';
import {
  clearTokenFromStorage,
  getLoginRequestFromStorage,
  getSessionFromStorage,
  getTokenFromStorage,
} from './utills/storage';
import { TMemberScreeningHistoryResponse } from '@repo/utils';

const baseUrl = config('API_BASE_URL');

function useAuthenticatedHeaders(): RawAxiosRequestHeaders {
  return {
    'Content-Type': 'application/json',
    Authorization: getTokenFromStorage(),
  };
}

function useUnauthenticatedHeaders(): RawAxiosRequestHeaders {
  return {
    'Content-Type': 'application/json',
  };
}

function createReferralMappingId(): string {
  if (typeof globalThis.crypto?.randomUUID !== 'function') {
    throw new Error('crypto.randomUUID is not supported in this environment');
  }

  return globalThis.crypto.randomUUID();
}

// Default handler for status codes that do not indicate success.
function validateResponseStatus(status?: number): boolean | never {
  // TODO - Make this more robust so that if the status is auth-related (401 / 403)
  //  we redirect to the authenticate route & refresh the user's token
  // TODO - Handle other bad response codes (503? 400?) if necessary
  // TODO - Determine whether or not necessary is spelled with one c or two
  const valid =
    status !== undefined &&
    status !== null &&
    status != 500 &&
    status != 401 &&
    status != 403;
  if (!valid) {
    console.error(
      `While making network request, received a bad status code. Code was ${status}`
    );

    if (status === 401) {
      // We were not authenticated, so clear token and redirect to login
      //  to restart the auth flow.
      clearTokenFromStorage();
      window.location.href = '/login';
    } else if (status === 403) {
      window.location.href = '/error';
    }
  }

  return valid;
}

function useDefaultConfig(): AxiosRequestConfig {
  return {
    headers: useAuthenticatedHeaders(),
    validateStatus: validateResponseStatus,
  };
}

function useUnauthenticatedConfig(): AxiosRequestConfig {
  return {
    headers: useUnauthenticatedHeaders(),
    validateStatus: validateResponseStatus,
  };
}

export namespace API {
  export async function requestToken(
    requestToken?: string
  ): Promise<RequestTokenResponse | AxiosError> {
    const route = '/sessions/request/token';
    const body = {
      session_id: getSessionFromStorage(), // Session ID or an empty string if we don't have one yet.
      request_id: getLoginRequestFromStorage(),
      request_token: requestToken ?? getTokenFromStorage(),
    };

    return axios
      .post(baseUrl + route, body, useUnauthenticatedConfig())
      .then((response) => {
        return response.data as RequestTokenResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return error;
      });
  }

  /**
   * Use the user's current (valid / not-expired) token to request a new, refreshed
   * token with identical claims and a pushed-back expiration timestamp.
   */
  export async function refreshToken(): Promise<
    RefreshTokenResponse | AxiosError
  > {
    const route = '/sessions/token/refresh';

    try {
      const response = await axios.post(
        baseUrl + route,
        undefined,
        useDefaultConfig()
      );

      return response.data as RefreshTokenResponse;
    } catch (error: any) {
      return error;
    }
  }

  export async function uhcSsoLogin(): Promise<{
    request_id: string;
    url: string;
  } | null> {
    const route = '/sessions/uhc/login';

    return axios
      .get(baseUrl + route, useUnauthenticatedConfig())
      .then((response) => {
        return response.data as { request_id: string; url: string };
      })
      .catch((error: AxiosError) => {
        console.error('Error while attempting SSO auth');
        console.error(error);
        return null;
      });
  }

  export async function getUserInfo(): Promise<UserInfoResponse | null> {
    const route = '/sessions/userInfo';
    const body = '';
    return axios({
      method: 'get',
      url: baseUrl + route,
      data: body,
      headers: useAuthenticatedHeaders(),
      validateStatus: validateResponseStatus,
    })
      .then((response) => {
        return response.data as UserInfoResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function searchMemberById(params: {
    memberId: string;
  }): Promise<HcpMemberSearchResponse | any> {
    const route = '/member/search';
    const body = {
      ...params,
      searchCriteria: 'ID', // Meaning search by member's plan identifier
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        if (response.data?.status === 'success') {
          return response.data as HcpMemberSearchResponse;
        }

        console.error(
          'Member search did not respond with an error, but response body was malformed. Response: ',
          response.data
        );
        return null;
      })
      .catch((error: AxiosError) => {
        console.error(error.message);

        if (
          error.response?.data &&
          (error.response?.data as any).status == 'error'
        ) {
          console.error((error.response?.data as any).message);
          return error.response.data as HcpMemberSearchResponse;
        }

        return null;
      });
  }

  export async function searchMemberByDetails(params: {
    firstName: string;
    lastName: string;
    dob: { year: number; month: number; day: number };
  }): Promise<HcpMemberSearchResponse | null> {
    const route = '/member/search';
    const body = {
      ...params,
      searchCriteria: 'DEMOGRAPHIC', // Meaning search by member's personal information
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as HcpMemberSearchResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function declineAllAssistance(requestBody: any) {
    const route = '/screening/declineAllAssistance';
    const body = {
      memberScreeningId: requestBody,
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function getQuestionsForQuestionnaire(requestBody: any) {
    const route = '/screening/getQuestions';
    const body = {
      ...requestBody,
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function getQuestionsBasedOnAssesmentID(requestBody: any) {
    const route = '/screening/getAssesmentQuestions';
    const body = {
      ...requestBody,
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function getIdentifiedNeeds(requestBody: any) {
    const route = '/screening/getNeeds';
    const body = {
      ...requestBody,
    };
    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }
  // advocate flags
  export async function getFeatureFlags(): Promise<FeatureFlags> {
    const route = '/flagsmith/flags';
    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }
  export async function saveIdentifiedNeeds(requestBody: any) {
    const route = '/screening/saveAssistance';
    const body = { ...requestBody };
    if (
      !requestBody ||
      typeof requestBody !== 'object' ||
      !requestBody.memberScreeningId ||
      !requestBody.needs
    ) {
      throw new Error('Both memberScreeningId and needs are required.');
    }

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
      });
  }

  export async function getCategoryForNeed(requestBody: {
    requestedNeedIds: string[];
    providerId: `${number}`;
  }) {
    const route = '/screening/getCategoryForNeed';
    const body = {
      ...requestBody,
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function getMemberScreeningHistory(
    memberId: any
  ): Promise<TMemberScreeningHistoryResponse | null> {
    const route = `/screening/memberscreeninghistory/${memberId}`;

    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        return response.data as TMemberScreeningHistoryResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function saveQuestionsForQuestionnaire(requestBody: any) {
    const route = '/screening/saveResponse';
    const body = {
      ...requestBody,
      searchCriteria: 'saveResponse', // Meaning saveResponse
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function getMemberReferralHistory(params: {
    firstName: string;
    lastName: string;
    dob: string;
    memberId: string;
    monthsOfHistory: 6 | 12 | 18;
  }): Promise<MemberHistoryResponse | AxiosError> {
    const route = '/sessions/referralHistoryV2';

    const body = {
      memberNameFirst: params.firstName,
      memberNameLast: params.lastName,
      memberDob: params.dob,
      memberId: params.memberId,
      monthsOfHistory: params.monthsOfHistory,
      sessionId: getSessionFromStorage(), // We don't need to send this, it is not required or used by the API.
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as MemberHistoryResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return error;
      });
  }

  export async function initializeSession(
    params: MemberInfo
  ): Promise<InitializeSessionResponse | any> {
    const route = '/sessions/initializeSessionLoggedIn';
    const body = params;

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as InitializeSessionResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function getSessionInfo(): Promise<SessionInfoResponse | any> {
    const route = '/sessions/sessionInfo';
    const body = {
      session_id: getSessionFromStorage(),
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as SessionInfoResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function checkDisableScreening(params: {
    lineOfBusiness: string;
    consumerId: string | undefined;
    zipcode: string | undefined;
  }): Promise<ApiResponse<boolean> | any> {
    const route = '/screening/checkDisableScreening';
    const body = params;

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as ApiResponse<string>;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function fetchProviderToUse(params: {
    lineOfBusiness: string | undefined;
    zipcode: string | undefined;
  }): Promise<ProviderToUseResponse | null> {
    const route = '/sessions/providerToUse';
    const body = params;

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as ProviderToUseResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function getFindhelpCategories(): Promise<FindhelpCategoriesResponse | null> {
    const route = '/taxonomy/data/AuntBertha';

    // Required in order to send "'Content-Type': 'application/json'" header with request.
    // If we don't send that header, our API returns a 500.
    // See https://github.com/axios/axios/issues/86#issuecomment-139638284 for Axios issue on this subject.
    return axios
      .request({
        url: baseUrl + route,
        method: 'GET',
        data: null,
        headers: useDefaultConfig().headers,
        validateStatus: validateResponseStatus,
      })
      .then((response) => {
        return response.data as FindhelpCategoriesResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function getFindhelpAttributeTagsAll(): Promise<FindhelpAttributeTagsResponse | null> {
    const route = '/programs/attributeTags/AuntBertha/all';

    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        return response.data as FindhelpAttributeTagsResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function getLocationSpecificFindhelpAttributeTags(
    zipcode: `${string}`
  ): Promise<ApiResponse<{ id: string; label: string }[]> | null> {
    const route = '/programs/attributeTags/AuntBertha';

    return axios
      .post(baseUrl + route, { zipcode }, useDefaultConfig())
      .then((response) => {
        return response.data as ApiResponse<{ id: string; label: string }[]>;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function skipAssessment(requestBody: any) {
    const route = '/screening/skipAssessment';
    const body = {
      memberId: requestBody.memberId,
      zipCode: requestBody.zipCode,
      reason: requestBody.reason,
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function authorizeFHIR(
    body: UniteUsFhirAuthRequest
  ): Promise<{ redirect_url: string } | null> {
    const route = '/fhir/oauth2/authorize';

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as { redirect_url: string };
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }


  export async function zipcodeSearch(requestBody: any) {
    const route = '/zipcodes/find/zipcode';
    const body = {
      ...requestBody,
    };

    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return {
          status: error.status,
          data: error.response?.data ?? error.response,
        };
      });
  }

  export async function getBenefitCheck(
    benefitPlanID: string,
    benefitPlanPlatform: string
  ): Promise<Benefitcheck | null> {
    const route = '/benefit/checkBenefits';
    return axios
      .post(
        baseUrl + route,
        { benefitPlanID, benefitPlanPlatform },
        useDefaultConfig()
      )
      .then((response) => {
        return response.data as Benefitcheck;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function getFindHelpProgramsUsingNeedandZip(params: {
    zipcode: string;
    page: number;
    attributeTags: string[];
    serviceTags: string[];
    sortBy: string;
    recordsPerPage: number;
  }): Promise<FindhelpProgramsResponse | null> {
    const route = '/programs/AuntBertha';

    return axios
      .post(baseUrl + route, params, useDefaultConfig())
      .then((response) => {
        return response.data as FindhelpProgramsResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
                return null;
      });
  }

  export async function getFindHelpProgramsUsingKeywords(params: {
    zipcode: string;
    page: number;
    terms: string;
    sortBy: string;
    recordsPerPage: number;
  }): Promise<FindhelpProgramsResponse | null> {
    const route = '/programs/AuntBertha';
    const body = {
      ...params,
      attributeTags: [],
      serviceTags: [],
    };
    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as FindhelpProgramsResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function getFindHelpProgramsUsingId(
    programId: string
  ): Promise<FindhelpProgramResponse | null> {
    const route = `/programs/AuntBertha/${programId}`;

    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        return response.data as FindhelpProgramResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function submitReferralFindhelp(
    referralinfo: FindhelpReferralRequest
  ): Promise<{ status: string } | null> {
    const route = '/programs/refer/auntBertha';
    const payload = {
      ...referralinfo,
      uuid: referralinfo.uuid ?? createReferralMappingId(),
    };

    return axios
      .post(baseUrl + route, payload, { ...useDefaultConfig() })
      .then((response) => {
        return response.data as { status: string };
      })
      .catch((error: AxiosError) => {
        console.error("Couldn't submit findhelp referral, error was", error);
        return null;
      });
  }

  export async function submitReferralUniteUS(
    referralinfo: UniteUSReferralRequest
  ): Promise<{ status: string } | null> {
    const route = '/programs/uniteus/referral';

    return axios
      .post(baseUrl + route, referralinfo, { ...useDefaultConfig() })
      .then((response) => {
        return response.data as { status: string };
      })
      .catch((error: AxiosError) => {
        console.error("Couldn't submit uniteus referral, error was", error);
        return null;
      });
  }

  export async function submitReferralCustom(
    referralinfo: FindhelpReferralRequest
  ): Promise<{ status: string } | null> {
    const route = '/programs/refer/customCBO';

    return axios
      .post(baseUrl + route, referralinfo, { ...useDefaultConfig() })
      .then((response) => {
        return response.data as { status: string };
      })
      .catch((error: AxiosError) => {
        console.error("Couldn't submit custom CBO referral, error was", error);
        return null;
      });
  }

  export async function getPrimaryCategories(
    zipcode: string
  ): Promise<PrimaryCategoriesResponse | null> {
    const route = '/taxonomy/data/PrimaryCategories';

    return axios
      .post(baseUrl + route, { zipcode }, useDefaultConfig())
      .then((response) => {
        return response.data as PrimaryCategoriesResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }
  export async function getBenefitPlan(
    benefitPlanID: string,
    benefitPlanPlatform: string
  ): Promise<BenefitPlan | null> {
    const route = '/benefit/categories';
    return axios
      .post(
        baseUrl + route,
        { benefitPlanID, benefitPlanPlatform },
        useDefaultConfig()
      )
      .then((response) => {
        return response.data as BenefitPlan;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }
  export async function getBenefitDetailsByBenefitId(
    benefitId: string,
    benefitPlanPlatform: string
  ): Promise<BenefitDetailsResponse | null> {
    const route = '/benefit/details';

    return axios
      .post(
        baseUrl + route,
        { benefitID: benefitId, benefitPlanPlatform: benefitPlanPlatform },
        { ...useDefaultConfig() }
      )
      .then((response) => {
        return response.data as BenefitDetailsResponse;
      })
      .catch((error: AxiosError) => {
        console.error(
          `Couldn't get information related to benefitId ${benefitId} error was ${error}`
        );
        return null;
      });
  }

  export async function getBenefitSearchResults(
    benefitPlanID: string,
    benefitPlanPlatform: string,
    searchTerm: string
  ): Promise<BenefitSearchResponse | null> {
    const route = '/benefit/search';
    const body = {
          benefitPlanId: benefitPlanID,
          benefitPlanPlatform,
          searchTerm,
    };

    return axios
      .post(baseUrl + route, body, { ...useDefaultConfig() })
      .then((response) => {
        return response.data as BenefitSearchResponse;
      })
      .catch((error: AxiosError) => {
        console.error(
          `Couldn't get information related to ${searchTerm} error was ${error}`
        );
        return null;
      });
  }

  export async function getEligibleIncome(
    state: string,
    status: string
  ): Promise<EligibleIncomeResponse | null> {
    const route = '/screening/getEligibleIncome';
    const body = {
      state,
      status,
    };

    return axios
      .post(baseUrl + route, body, { ...useDefaultConfig() })
      .then((response) => {
        return response.data as EligibleIncomeResponse;
      })
      .catch((error: AxiosError) => {
        console.error(`Couldn't get information error was ${error}`);
        return null;
      });
  }

  export async function getsaveQuizResponse(
    memberId: string,
    zipcode: string,
    questionsResponse: string[],
    memberFirstName: string,
    memberLastName: string
  ): Promise<saveQuizResponse | null> {
    const route = '/screening/saveQuizResponse';
    const body = {
      memberId,
      zipcode,
      quizId: '3',
      quizName: 'MSP-LIS',
      questionsResponse,
      memberFirstName,
      memberLastName,
    };

    return axios
      .post(baseUrl + route, body, { ...useDefaultConfig() })
      .then((response) => {
        return response.data as saveQuizResponse;
      })
      .catch((error: AxiosError) => {
        console.error(`Couldn't get information error was ${error}`);
        return null;
      });
  }

  // TO-DO: next story will integrate with backend
  export async function postSendEmail(
    emailAddress: string,
    emailBody: any,
    notes: string
  ) {
    const route = '/v1/mail/sendProgramDetailsMail';
    const body = {
      toEmails: [emailAddress],
      ccEmails: [],
      bccEmails: [],
      additionalInfo: notes,
      subject: 'Community Resource Information',
      programDetails: emailBody,
    };
    return axios
      .post(baseUrl + route, body, { ...useDefaultConfig() })
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        console.error(`Couldn't send email: ${error}`);
        return null;
      });
  }

  export async function submitSdohScreenerUpdate(
    originalRecord: UpdateScreenerRequest['record'],
    memberInfo: UpdateScreenerRequest['memberDetails'],
    updateType: 'Closed',
    updateText: UpdateScreenerRequest['updateText']
  ): Promise<ApiResponse<undefined> | null> {
    const route = '/sdoh-record/screener';

    const body: UpdateScreenerRequest = {
      record: originalRecord,
      memberDetails: memberInfo,
      updateType,
      updateText,
    };

    try {
      const resp = await axios.post(baseUrl + route, body, useDefaultConfig());
      return resp.data as ApiResponse<undefined>;
    } catch (error: any) {
      if (error instanceof AxiosError && 'message' in error) {
        console.error(
          'Error while attempting to update screener ',
          error.message
        );
      } else {
        console.error('Error while attempting to update screener ', error);
      }
    }

    return null;
  }

  export async function submitSdohReferralUpdate(
    originalRecord: UpdateReferralRequest['record'],
    memberInfo: UpdateReferralRequest['memberDetails'],
    updateType: UpdateReferralRequest['updateType'],
    updateText: UpdateReferralRequest['updateText']
  ): Promise<ApiResponse<undefined> | null> {
    const route = '/sdoh-record/referral';

    const body: UpdateReferralRequest = {
      record: originalRecord,
      memberDetails: memberInfo,
      updateType,
      updateText,
    };

    try {
      const resp = await axios.post(baseUrl + route, body, useDefaultConfig());
      return resp.data as ApiResponse<undefined>;
    } catch (error: any) {
      if (error instanceof AxiosError && 'message' in error) {
        console.error(
          'Error while attempting to update referral ',
          error.message
        );
      } else {
        console.error('Error while attempting to update referral ', error);
      }
    }

    return null;
  }

  export async function submitPrograms(
    requestData: SuggestProgramEmailRequest
  ): Promise<ApiResponse<undefined> | null> {
    const route = '/v1/mail/suggestProgram';

    return axios
      .post(baseUrl + route, requestData, useDefaultConfig())
      .then((response) => {
        return response.data as ApiResponse<undefined>;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function saveFavorite(
    programId: string,
    folderName: string
  ): Promise<SaveAndDeleteFavoriteResponse | null> {
    const route = '/favorites/saveFavorite';
    const body = { programId, folderName };
    return axios
      .post(baseUrl + route, body, useDefaultConfig())
      .then((response) => {
        return response.data as SaveAndDeleteFavoriteResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function deleteFavorite(
    folderId: string,
    programId: string
  ): Promise<SaveAndDeleteFavoriteResponse | null> {
    const route = '/favorites/folder/' + folderId + '/program/' + programId;
    return axios
      .post(baseUrl + route, {}, useDefaultConfig())
      .then((response) => {
        return response.data as SaveAndDeleteFavoriteResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function getAllFavorites(): Promise<GetAllFavoritesResponse | null> {
    const route = '/favorites/getAllFavorites';
    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        return response.data as GetAllFavoritesResponse;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function getUniteUsCategories(): Promise<ApiResponse<
    UniteUsService[]
  > | null> {
    const route = '/programs/uniteus/services';
    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        return response.data as ApiResponse<UniteUsService[]>;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }
  export async function getUniteUsFilters(): Promise<ApiResponse<
    UniteUsFilter[]
  > | null> {
    const route = '/programs/uniteus/filters';
    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        return response.data as ApiResponse<UniteUsFilter[]>;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }

  export async function searchUniteUsPrograms({
    categoryUuid,
    zipcode,
    page,
    recordsPerPage,
    attributeFilters, // TODO - Support for filters
    sortBy,
    distanceRadius,
    searchkeyword
  }: {
    categoryUuid: string;
    zipcode: string;
    page: number;
    recordsPerPage: number;
    attributeFilters: string[];
    sortBy: 'relevance' | 'distance';
    distanceRadius: number;
    searchkeyword:string|null
  }): Promise<ApiResponse<UniteUsProgramSearchResults> | null> {
    const route = '/programs/uniteus';

    const uniqueFilters = Array.from(new Set(attributeFilters));

    const req = {
      categories: categoryUuid!=null?[categoryUuid]:[],
      zipcode,
      page,
      page_size: recordsPerPage,
      page_number: page,
      sort_by: sortBy,
      distance_radius: distanceRadius ?? undefined,
      filters: uniqueFilters,
      searchkeyword:searchkeyword ?? null
    };

    return axios
      .post(baseUrl + route, req, useDefaultConfig())
      .then((response) => {
        return response.data as ApiResponse<UniteUsProgramSearchResults>;
      })
      .catch((error) => {
        console.error(error);
        return null;
      });
  }

  export async function submitBulkReferralFindhelp(
    bulkreferralinfo: FindhelpReferralRequest[]
  ): Promise<BulkReferralResult | null> {
    const route = '/programs/bulkReferrals/auntBertha';
    const payload = bulkreferralinfo.map((referral) => ({
      ...referral,
      uuid: referral.uuid ?? createReferralMappingId(),
    }));

    return axios
      .post(baseUrl + route, payload, { ...useDefaultConfig() })
      .then((response) => {
        return response.data as BulkReferralResult;
      })
      .catch((error: AxiosError) => {
        console.error("Couldn't submit findhelp referral, error was", error);
        return null;
      });
  }
  export async function submitBenefitReferral(
    referralinfo: BenefitReferralRequest
  ): Promise<ApiResponse<undefined> | null> {
    const route = '/sdoh-record/benefitReferral';
    return axios
    .post(baseUrl + route , referralinfo, { ...useDefaultConfig() })
    .then((response) => {
      return response.data as ApiResponse<undefined>;
    })
    .catch((error: AxiosError) => {
      console.error("Couldn't submit benefit referral, error was", error);
      return null;
    });
  }

  export async function getAzureMapsCredentials(): Promise<{
    subscriptionKey: string;
    clientId?: string;
  } | null> {
    const route = '/azuremaps/apikey';

    return axios
      .get(baseUrl + route, useDefaultConfig())
      .then((response) => {
        // Handle different response formats from backend
        if (typeof response.data === 'string') {
          // Backend returns just the key as a string
          return { subscriptionKey: response.data };
        } else if (response.data?.subscriptionKey) {
          // Backend returns structured object with subscriptionKey property
          return response.data;
        } else if (response.data?.apiKey) {
          // Backend might use 'apiKey' property name
          return { subscriptionKey: response.data.apiKey };
        } else {
          console.error('Unexpected Azure Maps response format:', response.data);
          return null;
        }
      })
      .catch((error: AxiosError) => {
        console.error(error);
        return null;
      });
  }
}
